import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateRewardEvent } from "@factory/query/reward-event.query";
import {
  RewardEventFormRule,
  RewardEventFormSchema,
} from "@factory/form/form.rule";
import { rewardEventFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewRewardEvent() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreateRewardEvent } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New RewardEvent is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addRewardEvent, isLoading: isCreating } =
    useQyCreateRewardEvent(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<RewardEventFormSchema>({
    defaultValues: rewardEventFormDefault,
    resolver: zodResolver(RewardEventFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: RewardEventFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreateRewardEvent(updatedForm);
    addRewardEvent(formData);
  };
  const handleValidateError = (err: FieldErrors<RewardEventFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
